import type { IncidentAuditEntry } from "@kmitl/shared";
import { WarnIcon } from "./Icons";

const KIND_LABEL: Record<string, { label: string; color: string }> = {
  acknowledge: { label: "ACK",       color: "var(--data)" },
  request:     { label: "REQUEST",   color: "var(--accent)" },
  inform:      { label: "INFO",      color: "var(--text-2)" },
  completed:   { label: "COMPLETED", color: "var(--good)" },
  blocked:     { label: "BLOCKED",   color: "var(--bad)" },
};

function fmtAgo(iso: string | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / (60 * 24))}d ago`;
}

function fmtDuration(ms: number | undefined): string {
  if (!ms || !Number.isFinite(ms)) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s later`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m later`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h later`;
  return `${Math.round(h / 24)}d later`;
}

interface Props {
  entries: IncidentAuditEntry[];
  emptyMessage: string;
}

export function IncidentAuditThread({ entries, emptyMessage }: Props) {
  return (
    <ol className="incident-thread" aria-label="Audit log thread">
      {entries.length === 0 && (
        <li className="incident-thread-empty caption">{emptyMessage}</li>
      )}
      {entries.map((entry, i) => {
        const kindMeta = entry.kind ? KIND_LABEL[entry.kind] : null;
        const actorTag =
          entry.actor === "citizen" ? "CITIZEN"
            : entry.actor === "admin" ? "ADMIN"
              : entry.actor === "ai" ? "AI"
                : entry.actor === "system" ? "SYSTEM"
                  : "STAFF";
        return (
          <li key={i} className={`incident-thread-entry actor-${entry.actor ?? "system"}`}>
            <div className="incident-thread-head">
              <span className="incident-thread-actor mono">{actorTag}</span>
              {entry.name && <span className="incident-thread-name">{entry.name}</span>}
              {kindMeta && (
                <span
                  className="incident-thread-kind mono"
                  style={{ borderColor: kindMeta.color, color: kindMeta.color }}
                >
                  {kindMeta.label}
                </span>
              )}
              {entry.type === "status_change" && entry.from && entry.to && (
                <span className="incident-thread-status mono">
                  {entry.from.toUpperCase()} → {entry.to.toUpperCase()}
                </span>
              )}
              {entry.type === "staff_message" && entry.delivered === false && (
                <span className="incident-thread-undelivered mono" title="Push to citizen's LINE failed">
                  <WarnIcon size={11} /> undelivered
                </span>
              )}
              <span className="incident-thread-ts mono caption" title={entry.ts}>
                {fmtAgo(entry.ts)} {fmtDuration(entry.sincePrevMs)}
              </span>
            </div>
            {entry.text && <p className="incident-thread-text">{entry.text}</p>}
            {entry.imageUrl && (
              <img className="incident-thread-img" src={entry.imageUrl} alt="" loading="lazy" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
